import { z } from 'zod';
import type { Permission } from '../permissions';
import { ExpoPushTokenSchema } from './admin-push.schemas';

export const AdminAuthenticatedUserSchema = z.object({
  id: z.string().min(1),
  email: z.email(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  /** Permissions the current admin holds. Used by the web-app to hide controls per ADR 0015. */
  permissions: z.array(z.string().min(1)) as unknown as z.ZodType<Permission[]>,
});

export type AdminAuthenticatedUser = z.infer<typeof AdminAuthenticatedUserSchema>;

export const AdminSignInProviderSchema = z.enum(['authkit', 'google', 'microsoft']);
export type AdminSignInProvider = z.infer<typeof AdminSignInProviderSchema>;

// Operators sign in with Google only (IMM-1422). The enum stays so older clients that still send
// `authkit` or `microsoft` keep parsing; the backend ignores the value.
// RFC 7636: S256 challenge = base64url(SHA-256(verifier)), unpadded, always 43 chars.
const PkceChallengeSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
// RFC 7636 §4.1: 43–128 unreserved characters.
const PkceVerifierSchema = z.string().regex(/^[A-Za-z0-9._~-]{43,128}$/);

export const AdminSignInQuerySchema = z.object({
  provider: AdminSignInProviderSchema.default('google'),
  codeChallenge: PkceChallengeSchema.optional(),
});

export const AdminSignInResponseSchema = z.object({
  url: z.url(),
});

export const AdminCallbackBodySchema = z.object({
  code: z.string().min(1),
  state: z.string().min(1).optional(),
  codeVerifier: PkceVerifierSchema.optional(),
});

export const AdminCallbackResponseSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1).optional(),
});

export const AdminRefreshBodySchema = z.object({ refreshToken: z.string().min(1).optional() }).strict();

export const AdminRefreshResponseSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1).optional(),
});

export const AdminMobileCallbackQuerySchema = z.object({
  code: z.string().max(4096).optional(),
  state: z.string().max(4096).optional(),
  error: z.string().max(256).optional(),
});

export const AdminSignOutBodySchema = z.object({ pushToken: ExpoPushTokenSchema.optional() }).strict();

export const AdminSignOutResponseSchema = z.object({
  url: z.url(),
});

export const AdminMeResponseSchema = z.object({
  user: AdminAuthenticatedUserSchema,
});

export type AdminMeResponse = z.infer<typeof AdminMeResponseSchema>;
